/**
 * Promo Code Admin API
 * Admin endpoints for managing marketing promo codes.
 *
 * Uses `apiClient` which auto-prefixes `/v1/admin`.
 */

import apiClient from './client';

// ============================================================================
// Types
// ============================================================================

export type PromoCodeAudience = 'all_users' | 'never_subscribed' | 'new_users';

export type PromoCodeStatus = 'active' | 'paused' | 'archived';

export interface PromoCodeResponse {
  id: string;
  code: string;
  free_days: number;
  audience: PromoCodeAudience;
  new_user_window_days: number | null;
  redeem_start_at: string;
  redeem_end_at: string;
  max_uses: number | null;
  used_count: number;
  status: PromoCodeStatus;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PromoCodeListResponse {
  total: number;
  codes: PromoCodeResponse[];
}

export interface PromoCodeCreate {
  code: string;
  free_days: number;
  audience: PromoCodeAudience;
  new_user_window_days?: number | null;
  redeem_start_at: string;
  redeem_end_at: string;
  max_uses?: number | null;
  description?: string | null;
}

/**
 * Partial update payload. The backend `PromoCodeUpdate` model forbids `code`
 * (and any other extra fields) — never include `code` here.
 */
export interface PromoCodeUpdate {
  free_days?: number;
  audience?: PromoCodeAudience;
  new_user_window_days?: number | null;
  redeem_start_at?: string;
  redeem_end_at?: string;
  max_uses?: number | null;
  status?: PromoCodeStatus;
  description?: string | null;
}

export interface PromoCodeAnalytics {
  code: string;
  redeemers: number;
  conversions: number;
  conversion_rate: number;
}

export interface ListPromoCodesParams {
  limit?: number;
  offset?: number;
}

// ============================================================================
// API Functions
// ============================================================================

export const promoCodesApi = {
  list: async (params: ListPromoCodesParams = {}): Promise<PromoCodeListResponse> => {
    const queryParams = new URLSearchParams();
    if (params.limit != null) queryParams.set('limit', params.limit.toString());
    if (params.offset != null) queryParams.set('offset', params.offset.toString());

    const query = queryParams.toString();
    const response = await apiClient.get(`/promo-codes${query ? `?${query}` : ''}`);
    return response.data;
  },

  create: async (payload: PromoCodeCreate): Promise<PromoCodeResponse> => {
    const response = await apiClient.post('/promo-codes', payload);
    return response.data;
  },

  update: async (id: string, payload: PromoCodeUpdate): Promise<PromoCodeResponse> => {
    const response = await apiClient.put(`/promo-codes/${id}`, payload);
    return response.data;
  },

  getAnalytics: async (id: string): Promise<PromoCodeAnalytics> => {
    const response = await apiClient.get(`/promo-codes/${id}/analytics`);
    return response.data;
  },
};
